import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit2,
  Calendar,
  MapPin,
  Users,
  DollarSign,
  Trophy,
  Star,
  Mail,
  Phone,
  FileText,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { getTournament, deleteTournament } from "../services/tournamentService";
import { getAdminData } from "../services/adminAuth";
import AdminTournamentDraw from "./AdminTournamentDraw";

const AdminTournamentView = () => {
  const { isDarkMode } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDraw, setShowDraw] = useState(false);
  const adminData = getAdminData();

  useEffect(() => {
    fetchTournament();
  }, [id]);

  const fetchTournament = async () => {
    try {
      setLoading(true);
      const response = await getTournament(id);
      setTournament(response.data);
    } catch (error) {
      console.error("Error fetching tournament:", error);
      alert("Failed to load tournament");
      navigate("/admin/tournaments");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${tournament.tournament_name}"?\n\nThis action cannot be undone!`,
      )
    ) {
      return;
    }

    try {
      await deleteTournament(tournament.id, adminData.id);
      alert("Tournament deleted successfully");
      navigate("/admin/tournaments");
    } catch (error) {
      console.error("Error deleting tournament:", error);
      alert(error.message || "Failed to delete tournament");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleString("en-ZA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "published":
      case "registration_open":
        return isDarkMode
          ? "bg-fsgu-green-900/30 text-fsgu-green-400"
          : "bg-fsgu-green-100 text-fsgu-green-700";
      case "draft":
        return isDarkMode
          ? "bg-fsgu-gold-900/30 text-fsgu-gold-400"
          : "bg-fsgu-gold-100 text-fsgu-gold-700";
      case "registration_closed":
      case "in_progress":
        return isDarkMode
          ? "bg-fsgu-blue-900/30 text-fsgu-blue-400"
          : "bg-fsgu-blue-100 text-fsgu-blue-700";
      case "completed":
        return isDarkMode
          ? "bg-fsgu-gray-700 text-fsgu-silver-400"
          : "bg-fsgu-silver-200 text-fsgu-silver-700";
      case "cancelled":
        return isDarkMode
          ? "bg-red-900/30 text-red-400"
          : "bg-red-100 text-red-700";
      default:
        return isDarkMode
          ? "bg-fsgu-gray-700 text-fsgu-silver-300"
          : "bg-fsgu-silver-200 text-fsgu-silver-700";
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-fsgu-gray-900" : "bg-fsgu-silver-50"
        }`}
      >
        <div className="text-center">
          <div
            className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-solid border-current border-r-transparent mb-4 ${
              isDarkMode ? "text-fsgu-green-400" : "text-fsgu-green-500"
            }`}
          ></div>
          <p
            className={
              isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
            }
          >
            Loading tournament...
          </p>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return null;
  }

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-fsgu-gray-900" : "bg-fsgu-silver-50"
      }`}
    >
      {/* Header */}
      <div
        className={`sticky top-0 z-30 border-b ${
          isDarkMode
            ? "bg-fsgu-gray-900/95 border-fsgu-gray-700 backdrop-blur-sm"
            : "bg-fsgu-silver-50/95 border-fsgu-silver-200 backdrop-blur-sm"
        }`}
      >
        <div className="container-custom py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Link
                to="/admin/tournaments"
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-fsgu-gray-800 text-fsgu-silver-300"
                    : "hover:bg-fsgu-silver-100 text-fsgu-silver-600"
                }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1
                  className={`text-3xl sm:text-4xl font-bold font-cursive ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}
                >
                  Tournament Details
                </h1>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                  }`}
                >
                  View complete tournament information
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to={`/admin/tournaments/edit/${tournament.id}`}
                className="btn btn-primary inline-flex items-center"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Edit Tournament</span>
                <span className="sm:hidden">Edit</span>
              </Link>

              <button
                onClick={handleDelete}
                className="btn btn-outline-danger inline-flex items-center"
              >
                <Trash2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-custom py-6 space-y-6">
        {/* Tournament Header Card */}
        <div
          className={`p-6 rounded-xl border ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}
        >
          <div className="flex flex-col lg:flex-row gap-6">
            {tournament.poster_image_url && (
              <div className="lg:w-72 flex-shrink-0">
                <img
                  src={tournament.poster_image_url}
                  alt={tournament.tournament_name}
                  className="w-full rounded-xl shadow-lg object-cover"
                />
              </div>
            )}

            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2
                      className={`text-3xl font-bold ${
                        isDarkMode
                          ? "text-fsgu-silver-100"
                          : "text-fsgu-silver-900"
                      }`}
                    >
                      {tournament.tournament_name}
                    </h2>
                    {tournament.is_featured && (
                      <Star
                        className="w-6 h-6 text-fsgu-gold-500"
                        fill="currentColor"
                      />
                    )}
                  </div>
                  {tournament.tournament_short_name && (
                    <p
                      className={`text-lg mb-2 ${
                        isDarkMode
                          ? "text-fsgu-silver-400"
                          : "text-fsgu-silver-600"
                      }`}
                    >
                      {tournament.tournament_short_name}
                    </p>
                  )}
                </div>

                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(
                    tournament.status,
                  )}`}
                >
                  {tournament.status.replace("_", " ").toUpperCase()}
                </span>
              </div>

              {tournament.description && (
                <p
                  className={`text-base mb-6 leading-relaxed ${
                    isDarkMode ? "text-fsgu-silver-300" : "text-fsgu-silver-700"
                  }`}
                >
                  {tournament.description}
                </p>
              )}

              {/* Quick Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-fsgu-gray-900/50" : "bg-fsgu-silver-50"
                  }`}
                >
                  <div
                    className={`text-xs font-medium mb-1 ${
                      isDarkMode
                        ? "text-fsgu-silver-500"
                        : "text-fsgu-silver-500"
                    }`}
                  >
                    Category
                  </div>
                  <div
                    className={`font-semibold ${
                      isDarkMode
                        ? "text-fsgu-silver-200"
                        : "text-fsgu-silver-900"
                    }`}
                  >
                    {tournament.category.replace(/_/g, " ")}
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-fsgu-gray-900/50" : "bg-fsgu-silver-50"
                  }`}
                >
                  <div
                    className={`text-xs font-medium mb-1 ${
                      isDarkMode
                        ? "text-fsgu-silver-500"
                        : "text-fsgu-silver-500"
                    }`}
                  >
                    Format
                  </div>
                  <div
                    className={`font-semibold ${
                      isDarkMode
                        ? "text-fsgu-silver-200"
                        : "text-fsgu-silver-900"
                    }`}
                  >
                    {tournament.format || "Not specified"}
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-fsgu-gray-900/50" : "bg-fsgu-silver-50"
                  }`}
                >
                  <div
                    className={`text-xs font-medium mb-1 ${
                      isDarkMode
                        ? "text-fsgu-silver-500"
                        : "text-fsgu-silver-500"
                    }`}
                  >
                    Rounds
                  </div>
                  <div
                    className={`font-semibold ${
                      isDarkMode
                        ? "text-fsgu-silver-200"
                        : "text-fsgu-silver-900"
                    }`}
                  >
                    {tournament.rounds}
                  </div>
                </div>

                <div
                  className={`p-4 rounded-lg ${
                    isDarkMode ? "bg-fsgu-gray-900/50" : "bg-fsgu-silver-50"
                  }`}
                >
                  <div
                    className={`text-xs font-medium mb-1 ${
                      isDarkMode
                        ? "text-fsgu-silver-500"
                        : "text-fsgu-silver-500"
                    }`}
                  >
                    Draw Entries
                  </div>
                  <div
                    className={`font-semibold ${
                      isDarkMode
                        ? "text-fsgu-silver-200"
                        : "text-fsgu-silver-900"
                    }`}
                  >
                    {tournament.draw_count || 0}
                    {tournament.field_size && ` / ${tournament.field_size}`}
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {tournament.is_championship && (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isDarkMode
                        ? "bg-fsgu-gold-900/30 text-fsgu-gold-400"
                        : "bg-fsgu-gold-100 text-fsgu-gold-700"
                    }`}
                  >
                    <Trophy className="w-3 h-3 mr-1" />
                    Championship
                  </span>
                )}
                {tournament.is_qualifier && (
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isDarkMode
                        ? "bg-fsgu-blue-900/30 text-fsgu-blue-400"
                        : "bg-fsgu-blue-100 text-fsgu-blue-700"
                    }`}
                  >
                    Qualifier
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tournament Draw Section - Collapsible */}
        <div
          className={`rounded-xl border overflow-hidden ${
            isDarkMode
              ? "bg-fsgu-gray-800 border-fsgu-gray-700"
              : "bg-fsgu-white border-fsgu-silver-200 shadow-sm"
          }`}
        >
          <button
            onClick={() => setShowDraw(!showDraw)}
            className={`w-full p-6 flex items-center justify-between transition-colors ${
              isDarkMode
                ? "hover:bg-fsgu-gray-700/50"
                : "hover:bg-fsgu-silver-50"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  isDarkMode ? "bg-fsgu-blue-500/20" : "bg-fsgu-blue-500/10"
                }`}
              >
                <Users
                  className={`w-5 h-5 ${
                    isDarkMode ? "text-fsgu-blue-400" : "text-fsgu-blue-600"
                  }`}
                />
              </div>
              <div className="text-left">
                <h3
                  className={`text-lg font-semibold ${
                    isDarkMode ? "text-fsgu-silver-100" : "text-fsgu-silver-900"
                  }`}
                >
                  Tournament Draw
                </h3>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                  }`}
                >
                  Manage player entries and tee times
                </p>
              </div>
            </div>
            {showDraw ? (
              <ChevronUp
                className={`w-5 h-5 ${
                  isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                }`}
              />
            ) : (
              <ChevronDown
                className={`w-5 h-5 ${
                  isDarkMode ? "text-fsgu-silver-400" : "text-fsgu-silver-600"
                }`}
              />
            )}
          </button>

          {showDraw && (
            <div
              className={`p-6 border-t ${
                isDarkMode ? "border-fsgu-gray-700" : "border-fsgu-silver-200"
              }`}
            >
              <AdminTournamentDraw
                tournamentId={tournament.id}
                tournamentName={tournament.tournament_name}
              />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          <div className={`p-6 rounded-xl border ${
  isDarkMode
    ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
    : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
}`}>
  <h3 className={`text-lg font-semibold mb-6 flex items-center ${
    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
  }`}>
    <Calendar className="w-5 h-5 mr-2" />
    Tournament Dates
  </h3>

  <div>
    <div className={`font-medium text-lg ${
      isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
    }`}>
      {formatDate(tournament.start_date)}
      {tournament.start_date !== tournament.end_date && (
        <> - {formatDate(tournament.end_date)}</>
      )}
    </div>
    {tournament.starts_in_days > 0 && (
      <div className={`text-sm mt-2 inline-flex items-center px-2 py-1 rounded-full ${
        isDarkMode
          ? 'bg-fsgu-green-900/30 text-fsgu-green-400'
          : 'bg-fsgu-green-100 text-fsgu-green-600'
      }`}>
        Starts in {tournament.starts_in_days} days
      </div>
    )}
  </div>
</div>

          {/* Venue Information */}
          <div className={`p-6 rounded-xl border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
          }`}>
            <h3 className={`text-lg font-semibold mb-6 flex items-center ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              <MapPin className="w-5 h-5 mr-2" />
              Venue Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                }`}>
                  Venue
                </div>
                <div className={`font-semibold text-lg ${
                  isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                }`}>
                  {tournament.venue_name}
                </div>
              </div>

              {tournament.venue_address && (
                <div>
                  <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    Address
                  </div>
                  <div className={`font-medium ${
                    isDarkMode ? 'text-fsgu-silver-200' : 'text-fsgu-silver-900'
                  }`}>
                    {tournament.venue_address}
                    {tournament.venue_city && `, ${tournament.venue_city}`}
                  </div>
                </div>
              )}
            </div>

            {tournament.special_notes && (
              <div className={`mt-6 pt-4 border-t ${
                isDarkMode ? 'border-fsgu-gray-700' : 'border-fsgu-silver-200'
              }`}>
                <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                }`}>
                  Special Notes
                </div>
                <div className={isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'}>
                  {tournament.special_notes}
                </div>
              </div>
            )}
          </div>

          {/* Full Description */}
          {tournament.full_description && (
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <FileText className="w-5 h-5 mr-2" />
                Full Description
              </h3>
              <div className={`whitespace-pre-wrap leading-relaxed ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                {tournament.full_description}
              </div>
            </div>
          )}

          {/* Rules */}
          {tournament.rules && (
            <div className={`p-6 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200 shadow-sm'
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                <FileText className="w-5 h-5 mr-2" />
                Tournament Rules
              </h3>
              <div className={`whitespace-pre-wrap leading-relaxed ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                {tournament.rules}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTournamentView;
